import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Button, F0ButtonDropdown } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import {
  CardPin,
  CheckDouble as CheckDoubleIcon,
  Cross as CrossIcon,
} from '@factorialco/f0-react/icons/app'
import {
  BulkApproveApprovalsv2ReviewRequestDocument,
  BulkRejectApprovalsv2ReviewRequestDocument,
  BulkRequestChangesApprovalsv2ReviewRequestDocument,
  InboxTaskListItemCompletionFragmentDoc,
  InboxTasksReviewerDocument,
  TaskActivityDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import {
  TaskCustomComponent,
  TaskDefinition,
  TaskFooterComponent,
} from 'modules/inbox_tasks/lib/abstraction'
import { getExpensesExpensableMetadata } from 'modules/inbox_tasks/tasks/approval/ExpensesExpensable'
import { getProcurementPurchaseRequestMetadata } from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'
import { RESOURCE_COMPONENTS, RESOURCE_TITLES } from 'modules/inbox_tasks/tasks/approval/helpers'
import ReasonInput from 'modules/inbox_tasks/tasks/approvalsv2/ReasonInput'

type State = {
  additionalComments?: string
}

const isV2ResourceType = (
  typename: string
): typename is 'ProcurementPurchaseRequest' | 'ExpensesExpensable' => {
  return typename === 'ProcurementPurchaseRequest' || typename === 'ExpensesExpensable'
}

const formatAlertDate = (timestamp: string) => {
  const date = stringToDate(timestamp, 'backend_datetime')
  return i18nDateToString(date, 'ui_short_with_time')
}

const calculateDaysOverdue = (deadline: string | undefined): number => {
  if (!deadline) return 0

  const deadlineDate = stringToDate(deadline, 'backend')
  const daysOverdue = moment().startOf('day').diff(moment(deadlineDate).startOf('day'), 'days')

  return daysOverdue > 0 ? daysOverdue : 0
}

const ApprovalsV2TaskContent: TaskCustomComponent<'ApprovalsV2ReviewRequest', State> = ({
  affectee,
  dueOn,
  ...props
}) => {
  const { employee, access } = useCurrentSession()

  const taskable = 'taskable' in props ? props.taskable : undefined

  const approvalProcess = taskable?.approvalProcess

  const approvable = approvalProcess?.resource

  const resourceId = approvable && 'id' in approvable ? approvable.id : undefined

  const reason = taskable?.decisionReason || approvalProcess?.decisionReason
  const isRejected = approvalProcess?.decision === 'rejected'
  const isChangesRequested = taskable?.decision === 'changes_requested'
  const shouldShowAlert = isRejected || isChangesRequested
  const resourceTypename = approvable?.__typename ?? ''

  const owner = approvalProcess?.resourceOwner?.employeeName
  const ownerId = owner?.id

  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })

  const reviewerTime = taskable?.reviewedAt || ''
  const reviewerId = taskable?.reviewedById

  const isCurrentUserApprover = reviewerId === employee.id

  const { data: employeeData } = useQuery(InboxTasksReviewerDocument, {
    variables: { employeeId: reviewerId || 0 },
    skip: !reviewerId || isCurrentUserApprover,
  })

  const reviewerName =
    employeeData?.employees?.employee?.employeeName?.fullName ||
    (isCurrentUserApprover ? access.fullName : '')

  const daysOverdue = dueOn ? calculateDaysOverdue(dueOn) : 0

  const Content = useUnsafeMemo(() => {
    if (!resourceId || !resourceTypename) {
      return null
    }

    const Component = RESOURCE_COMPONENTS[resourceTypename]

    if (!Component) return null

    return (
      <Component
        id={resourceId}
        requesterContent={requesterContent}
        affectee={affectee}
        requesterEmployeeId={ownerId}
      />
    )
  }, [affectee, resourceTypename, resourceId, requesterContent, ownerId])

  return (
    <Box gap="s24" overflowX="hidden" flexGrow>
      {shouldShowAlert && !!reason && (
        <OneAlert
          variant={isRejected ? 'critical' : 'warning'}
          title={
            isRejected
              ? i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
                  reason: reason,
                })
              : i18n.t(
                  'procurement.purchase_requests.detail.timeline.changes_requested_alert.title',
                  {
                    reason: reason,
                  }
                )
          }
          description={
            reviewerName && reviewerTime
              ? `${reviewerName}, ${formatAlertDate(reviewerTime)}`
              : reviewerTime
              ? formatAlertDate(reviewerTime)
              : ''
          }
          icon={CardPin}
        />
      )}
      {daysOverdue > 0 && (
        <OneAlert
          variant="warning"
          title={i18n.tp('tasks.approvals.alerts.overdue.title', {
            count: daysOverdue,
          })}
          description=""
        />
      )}
      {Content}
    </Box>
  )
}

const ApprovalsV2TaskFooter: TaskFooterComponent<'ApprovalsV2ReviewRequest', State> = ({
  taskId,
  taskable,
  status,
}) => {
  const apolloClient = useApolloClient()

  const { company, employee } = useCurrentSession()

  const [isReasonInputExpanded, setIsReasonInputExpanded] = React.useState(false)
  const [actionMode, setActionMode] = React.useState<'reject' | 'request_changes'>('reject')

  const [bulkApprove] = useMutation(BulkApproveApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkApproveApprovalsv2ReviewRequest,
  })

  const [bulkReject] = useMutation(BulkRejectApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkRejectApprovalsv2ReviewRequest,
  })

  const [bulkRequestChanges] = useMutation(BulkRequestChangesApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkRequestChangesApprovalsv2ReviewRequest,
    refetchQueries: [TaskActivityDocument],
  })

  const handleSuccessApproveReject = useUnsafeCallback(() => {
    apolloClient.writeFragment({
      id: `TasksInboxTask:${taskId}`,
      fragment: InboxTaskListItemCompletionFragmentDoc,
      fragmentName: 'InboxTaskListItemCompletion',
      data: {
        __typename: 'TasksInboxTask',
        id: taskId,
        completedAt: new Date().toISOString(),
        status: 'done',
      },
    })

    apolloClient.refetchQueries({
      include: [TaskActivityDocument],
    })
  }, [apolloClient, taskId])

  const handleApprove = useUnsafeCallback(async () => {
    if (!taskable?.id) return

    const result = await bulkApprove({
      variables: {
        companyId: company.id,
        reviewRequestIds: [taskable.id],
        reviewerId: employee.id,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('settings.approvals.approval_action.flash_messages.approve'),
      })
      handleSuccessApproveReject()
    }

    return handled.success
  }, [taskable?.id, company.id, employee.id, bulkApprove, handleSuccessApproveReject])

  const handleReject = useUnsafeCallback(
    async (reason?: string) => {
      if (!taskable?.id) return

      const result = await bulkReject({
        variables: {
          companyId: company.id,
          reviewRequestIds: [taskable.id],
          reviewerId: employee.id,
          reason: reason,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.approvals.approval_action.flash_messages.reject'),
        })
        handleSuccessApproveReject()
      }

      return handled.success
    },
    [taskable?.id, company.id, employee.id, bulkReject, handleSuccessApproveReject]
  )

  const handleSendChanges = useUnsafeCallback(
    async (reason?: string) => {
      if (!taskable?.id) return false

      const result = await bulkRequestChanges({
        variables: {
          companyId: company.id,
          reviewRequestIds: [taskable.id],
          reviewerId: employee.id,
          reason: reason || undefined,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: 'An error occurred while requesting changes. Please try again.',
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: 'Changes requested successfully.',
        })
        handleSuccessApproveReject()
      }

      return handled.success
    },
    [taskable?.id, company.id, employee.id, bulkRequestChanges, handleSuccessApproveReject]
  )

  if (!taskable?.id || status === 'done') {
    return null
  }

  return (
    <Box flexDirection="row" gap="s12" justifyContent="flexEnd" flexGrow>
      {!isReasonInputExpanded && (
        <F0ButtonDropdown
          value="reject"
          variant="outline"
          items={[
            {
              value: 'reject',
              label: i18n.t('common.reject'),
              icon: CrossIcon,
            },
            {
              value: 'changes_requested',
              label: i18n.t('common.request_changes'),
            },
          ]}
          onClick={(value) => {
            if (value === 'reject') {
              setActionMode('reject')
              setIsReasonInputExpanded(true)
            } else if (value === 'changes_requested') {
              setActionMode('request_changes')
              setIsReasonInputExpanded(true)
            }
          }}
        />
      )}
      <ReasonInput
        onAction={(reason) => {
          if (actionMode === 'reject') {
            return handleReject(reason)
          } else {
            return handleSendChanges(reason)
          }
        }}
        isExpanded={isReasonInputExpanded}
        onCancel={() => {
          setIsReasonInputExpanded(false)
        }}
        textareaLabel={i18n.t('tasks.approvals.rejection_reason.label')}
        textareaPlaceholder={i18n.t('tasks.approvals.rejection_reason.placeholder')}
        confirmButtonLabel={
          actionMode === 'reject'
            ? i18n.t('tasks.approvals.rejection_reason.reject')
            : 'Send changes'
        }
      />
      {!isReasonInputExpanded && (
        <F0Button label={i18n.t('common.approve')} icon={CheckDoubleIcon} onClick={handleApprove} />
      )}
    </Box>
  )
}

export const ApprovalsV2TaskDefinition: TaskDefinition<'ApprovalsV2ReviewRequest', State> = {
  taskableTypename: 'ApprovalsV2ReviewRequest',
  initialState: {},
  component: ApprovalsV2TaskContent,
  footer: ApprovalsV2TaskFooter,
  getTitle: ({ info, affecteeName }) => {
    const approvableTypename = info?.approvalProcess?.resourceType

    if (!affecteeName || !approvableTypename) {
      return 'Review request'
    }

    const resourceTitleFunction = isV2ResourceType(approvableTypename)
      ? RESOURCE_TITLES[approvableTypename]
      : undefined

    const resource = resourceTitleFunction?.()
    const resourceText = typeof resource === 'string' ? resource : ''

    return i18n.t('common.approvals.title', {
      affectee: affecteeName,
      resource: resourceText,
    })
  },
  getMetadata: ({ info }) => {
    const approvable = info.approvalProcess?.resource

    if (!approvable) return []

    switch (approvable.__typename) {
      case 'ProcurementPurchaseRequest':
        return getProcurementPurchaseRequestMetadata(approvable)
      case 'ExpensesExpensable':
        return getExpensesExpensableMetadata(approvable)
      default:
        return []
    }
  },
}
